import {
  convertDateToYYYYMMDDFormat,
  FormFeedBack,
  NoDataComponent,
  PageWrapper,
  SimpleTableLoadingSkeleton,
  STATUSPAGE,
  TableVirtualized,
} from '@repo/ui';
import { useFormik } from 'formik';
import { makeSummaryReportByGroupColumns } from 'presentation/app/modules/Reports/common/columns/activities/summary-report-by-group-columns';
import { ReportsGeneralFilters } from 'presentation/app/modules/Reports/components/reports-general-filters';
import { useSummaryReportByGroup } from 'presentation/app/modules/Reports/hooks/useSummaryReportByGroup';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { Button, Card, Col, Input, Label, Row, Spinner } from 'reactstrap';
import * as Yup from 'yup';

const SummaryReportByGroup = () => {
  const {
    loading,
    hasFetchedData,
    selectedSubmitBtn,
    setSelectedSubmitBtn,
    requestHandleSummaryReportByGroup,
    requestHandleSummaryReportByGroupJson,
  } = useSummaryReportByGroup();

  const { summaryReportByGroup } = useAppSelector(
    (state) => state.summaryReportByGroup,
  );

  function getFirstDayOfMonth() {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }

  function getCurrentDate() {
    return new Date();
  }

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      entity: null,
      course: null,
      unit: null,
      semester: null,
      groups: [],
      ends: convertDateToYYYYMMDDFormat(getCurrentDate()),
      starts: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
    },

    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      starts: Yup.date().required('Campo é obrigatório!'),
      ends: Yup.date().required('Campo é obrigatório!'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        entity: values.entity?.value,
        course: values.course?.value,
        semester: values.semester?.value,
        curricularUnit: values.unit?.value,
        groups: values.groups.map((group) => group.value),
        ends: values.ends,
        starts: values.starts,
      };

      switch (selectedSubmitBtn) {
        case 'export-pdf':
          requestHandleSummaryReportByGroup({
            ...valuesToSubmit,
            type: '0',
          });
          break;
        case 'export-excel':
          requestHandleSummaryReportByGroup({
            ...valuesToSubmit,
            type: '1',
          });
          break;
        case 'export-json':
          requestHandleSummaryReportByGroupJson({
            ...valuesToSubmit,
            type: '2',
          });
          break;
        default:
          break;
      }
    },
  });

  const formattedData = summaryReportByGroup?.map((register, idx) => ({
    id: idx,
    ...register,
  }));

  return (
    <PageWrapper title="Por Grupo">
      <div className="mb-4">
        <h4>Por Grupo</h4>
        <p className="text-muted mb-0">
          Consulte o resumo de atividades organizadas por grupo.
        </p>
      </div>

      <Card>
        <ReportsGeneralFilters
          validation={validation}
          inputsChildren={
            <>
              <Col>
                <Row>
                  <Col lg={6} className="mb-3">
                    <Label htmlFor="initial-date" className="form-label">
                      Data inicial:
                    </Label>
                    <Input
                      type="date"
                      id="starts"
                      name="starts"
                      className="form-control"
                      value={validation.values.starts}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        !!(validation.touched && validation.errors?.starts)
                      }
                    />
                    <FormFeedBack
                      error={validation.errors?.starts}
                      isInvalid={
                        !!(validation.touched && validation.errors?.starts)
                      }
                    />
                  </Col>

                  <Col lg={6} className="mb-3">
                    <Label htmlFor="final-date" className="form-label">
                      Data final:
                    </Label>
                    <Input
                      type="date"
                      id="ends"
                      name="ends"
                      className="form-control"
                      value={validation.values.ends}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        !!(validation.touched && validation.errors?.ends)
                      }
                    />
                    <FormFeedBack
                      error={validation.errors?.ends}
                      isInvalid={
                        !!(validation.touched && validation.errors?.ends)
                      }
                    />
                  </Col>
                </Row>
              </Col>
            </>
          }
          buttonsChildren={
            <div className="d-flex gap-2 justify-content-end align-content-end">
              <Button
                onClick={() => {
                  setSelectedSubmitBtn('export-json');
                }}
                color="primary"
                type="submit"
                id="export-json"
                disabled={loading === STATUSPAGE.LOADING && true}
              >
                {selectedSubmitBtn === 'export-json' &&
                loading === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Visualizar
                    <i className="ri-computer-line mx-2"></i>
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setSelectedSubmitBtn('export-pdf');
                }}
                color="primary"
                type="submit"
                className="btn btn-light waves-effect waves-light"
                id={'save_button'}
                disabled={loading === STATUSPAGE.LOADING && true}
              >
                {selectedSubmitBtn === 'export-pdf' &&
                loading === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Exportar para PDF
                    <i className="las la-file-pdf mx-2"></i>
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setSelectedSubmitBtn('export-excel');
                }}
                color="primary"
                type="submit"
                id={'save_button'}
                className="btn btn-light waves-effect waves-light"
                disabled={loading === STATUSPAGE.LOADING && true}
              >
                {selectedSubmitBtn === 'export-excel' &&
                loading === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Exportar para Excel
                    <i className="ms-2 ri-file-excel-2-line"></i>
                  </>
                )}
              </Button>
            </div>
          }
        />
        {summaryReportByGroup?.length === 0 &&
          hasFetchedData &&
          loading === STATUSPAGE.DONE && (
            <div className="p-4">
              <NoDataComponent />
            </div>
          )}

        {loading === STATUSPAGE.LOADING && <SimpleTableLoadingSkeleton />}

        {loading === STATUSPAGE.DONE && summaryReportByGroup?.length > 0 && (
          <TableVirtualized
            columns={makeSummaryReportByGroupColumns()}
            data={formattedData}
          />
        )}
      </Card>
    </PageWrapper>
  );
};

export default SummaryReportByGroup;
